import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IBook } from '../../types/datatypes';
import { FeatureProps } from '../../types/feature-props.interface';
import { BookForm } from './book-form';
import { BooksService } from '../../core/services/data/books.service';
import { UserService } from '../../core/services/data/user.service';
import { UtilitiesService } from '../../core/services/utilities.service';

@Component({
	selector: 'app-shared-edit-book',
	templateUrl: './edit-book.component.html',
	styleUrls: ['./edit-book.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})

export class SharedEditBookComponent implements OnInit {
	@Input() type = 'promostack';

	@Input() bookType: string;

	@Input() set book(book: IBook) {
		this._book = book;
		this.initBookForm();
	}

	@Input() bookPromoPrice = 0;

	@Input() kindleUnlimited = false;

	@Input() kindleCountdownDeal = false;

	@Input() purchaserInfo: {
		email: string;
		firstName: string;
		lastName: string;
		terms?: boolean;
		subscribe?: boolean;
	} = {
			firstName: '',
			lastName: '',
			email: '',
		};

	@Input() priceRange: { min: number; max: number } = null;

	@Input() authorStoreEnabled = false;

	@Output() bookModified = new EventEmitter<IBook>();

	@Output() featurePropertiesModified = new EventEmitter<FeatureProps>();

	@Output() linkBlur = new EventEmitter();

	@Output() linkKeyDown = new EventEmitter();

	@Output() seriesChange = new EventEmitter<{ link: string; price: number }[]>();

	@Output() valid = new EventEmitter<boolean>();

	@ViewChild('authorstoreField') authorstoreField: ElementRef;

	@ViewChild('appleField') appleField: ElementRef;

	@ViewChild('nookField') nookField: ElementRef;

	@ViewChild('koboField') koboField: ElementRef;

	@ViewChild('googleplayField') googleplayField: ElementRef;

	public seriesEnabled = true;

	public seriesInfo: { link: string; price: number }[] = [];

	public bookComponents = new BookForm(this.type);

	public editBookForm: UntypedFormGroup;

	public newLinkForm: UntypedFormGroup = new UntypedFormGroup({
		type: new UntypedFormControl(),
		link: new UntypedFormControl(),
	});

	public uploadError: string;

	public uploading = false;

	public showNewLinkForm = false;

	public defaultCoverImage = '/assets/img/defaults/default_book_image_400w_simple.jpg';

	public queryPromoPrice: number;

	public showPurchaserInfoFields = false;

	public priceError = false;

	public _book: IBook;

	private valueChanges$;

	get descriptionLength() {
		if (!this.editBookForm.controls.description.value) return 0;

		return this.editBookForm.controls.description.value.length;
	}

	constructor(
		private userService: UserService,
		private utilitiesService: UtilitiesService,
		private booksService: BooksService,
	) {
	}

	linkBlurEvent() {
		this.linkBlur.emit(this.editBookForm.controls.link.value);
	}

	linkKeyDownEvent(event) {
		this.linkKeyDown.emit(event);
	}

	ngOnInit(): void {
		this.initBookForm();

		if (!this.userService.me) {
			this.showPurchaserInfoFields = true;
		}
	}

	addSeriesInfo() {
		this.seriesInfo.push({ link: '', price: 0 });
	}

	triggerSeriesUpdate() {
		const series = [];

		for (const seriesItem of this.seriesInfo) {
			if (seriesItem.link) {
				series.push(seriesItem);
			}
		}

		this.seriesChange.emit(series);
	}

	initBookForm() {
		this.valueChanges$?.unsubscribe();

		this.editBookForm = new UntypedFormGroup({
			title: new UntypedFormControl(this._book?.title, [Validators.required, Validators.minLength(3)]),
			author: new UntypedFormControl(this._book?.author, [Validators.required, Validators.minLength(3)]),
			description: new UntypedFormControl(this._book?.description),
			promo_price: new UntypedFormControl(this.bookPromoPrice, [Validators.min(0)]),
			kindleUnlimited: new UntypedFormControl(this.kindleUnlimited),
			kindleCountdownDeal: new UntypedFormControl(this.kindleCountdownDeal),
			amazonLink: new UntypedFormControl(this._book?.amazonLink, [Validators.pattern('.*(?:dp|gp/product)/([A-Z0-9]{10}|null).*')]),
			apple: new UntypedFormControl(this._book?.whereAvailable.apple),
			nook: new UntypedFormControl(this._book?.whereAvailable.nook),
			kobo: new UntypedFormControl(this._book?.whereAvailable.kobo),
			googleplay: new UntypedFormControl(this._book?.whereAvailable.googleplay),
			authorstore: new UntypedFormControl(this._book?.whereAvailable.authorstore),
			bookLength: new UntypedFormControl(this._book?.bookLength),
			nameOfSeries: new UntypedFormControl(this._book?.nameOfSeries),
			numberOfBooksInSeries: new UntypedFormControl(this._book?.numberOfBooksInSeries),
			bookNumber: new UntypedFormControl(this._book?.bookNumber),
			isSeries: new UntypedFormControl(this._book?.isSeries),
			purchaserInfoEmail: new UntypedFormControl(
				this.purchaserInfo?.email || this.userService.me?.username, [Validators.required, Validators.email],
			),
			purchaserInfoFirstName: new UntypedFormControl(this.purchaserInfo?.firstName || this.userService.me?.firstName, [Validators.required]),
			purchaserInfoLastName: new UntypedFormControl(this.purchaserInfo?.lastName || this.userService.me?.lastName, [Validators.required]),
			terms: new UntypedFormControl(this.purchaserInfo?.terms),
			subscribe: new UntypedFormControl(this.purchaserInfo.subscribe),
			link: new UntypedFormControl(this._book?.amazonLink),
		});

		this.valueChanges$ = this.editBookForm.valueChanges.subscribe((item) => {
			item.description = this.utilitiesService.trimStr(item.description);

			const bookProperties = {
				title: item.title,
				author: item.author,
				description: item.description,
				amazonLink: item.amazonLink,
				whereAvailable: {
					apple: item.apple,
					nook: item.nook,
					kobo: item.kobo,
					googleplay: item.googleplay,
					authorstore: item.authorstore,
				},
				isSeries: item.isSeries,
				bookNumber: item.bookNumber,
				bookLength: item.bookLength,
				nameOfSeries: item.nameOfSeries,
				numberOfBooksInSeries: item.numberOfBooksInSeries,
				ASIN: this.booksService.getASIN(item.amazonLink),
			};

			if (this.priceRange) {
				this.priceError = item.promo_price > this.priceRange.max || item.promo_price < this.priceRange.min;
			}

			const book = Object.assign(this._book, bookProperties);

			this.valid.emit(this.editBookForm.valid);

			this.bookModified.emit(book);
			this.featurePropertiesModified.emit({
				kindleUnlimited: item.kindleUnlimited,
				kindleCountdownDeal: item.kindleCountdownDeal,
				promo_price: item.promo_price,
				purchaserInfoEmail: item.purchaserInfoEmail,
				purchaserInfoFirstName: item.purchaserInfoFirstName,
				purchaserInfoLastName: item.purchaserInfoLastName,
				description: item.description,
				terms: item.terms,
				subscribeToList: item.subscribe,
			});
		});

		this.addInputsForWhereAvailable();
	}

	addInputsForWhereAvailable() {
		if (!this._book) {
			return;
		}
		for (const [key, val] of Object.entries(this._book.whereAvailable)) {
			if (this._book.whereAvailable[key] && val) {
				this.addInput(key);
			}
		}
	}

	addInput(idLink) {
		const fieldRef = this.getFieldRef(idLink);
		if (fieldRef?.nativeElement) {
			fieldRef.nativeElement.style.display = 'block';
		}
		this._book[this.newLinkForm.value.type] = this.newLinkForm.value.link;
		this.newLinkForm.reset();
	}

	deleteInput(idLink) {
		const fieldRef = this.getFieldRef(idLink);
		if (fieldRef?.nativeElement) {
			fieldRef.nativeElement.style.display = 'none';
		}
		const object = { idLink: '' };

		this.editBookForm.patchValue(object);
		this.editBookForm.controls[idLink].setValue('');
	}

	bookCoverUpload(result) {
		if (result && this._book) {
			this._book.amazonImageUrl = result.publicUrl;
		}
	}

	bookCoverUploadError(error) {
		this.uploadError = error;
	}

	isUploadingBookCover(uploading) {
		this.uploading = uploading;
	}

	showAddLinkForm() {
		this.showNewLinkForm = true;
	}

	displayPromoPriceField() {
		return this.queryPromoPrice > 0;
	}

	private getFieldRef(idLink: string): ElementRef {
		const fieldMap = {
			authorstore: this.authorstoreField,
			apple: this.appleField,
			nook: this.nookField,
			kobo: this.koboField,
			googleplay: this.googleplayField,
		};
		return fieldMap[idLink];
	}
}
